<script setup>
import { ref, computed, onMounted } from 'vue'
import learningApiIssue from '@src/services/api/learning/issue'

const props = defineProps({
  token: {
    type: String,
    required: true
  }
})

const STATUS = {
  LOADING: 'loading',
  VALID: 'valid',
  INVALID: 'invalid',
  ERROR: 'error'
}

const status = ref(STATUS.LOADING)
const issue = ref(null)

const certificateNo = computed(() => issue.value?.certificate ?? '')

const traineeName = computed(() => {
  const name = issue.value?.name
  if (Array.isArray(name)) {
    return name.filter(Boolean).join('　')
  }
  return name ?? ''
})

const traineePref = computed(() => issue.value?.pref ?? '')

const fetchIssue = () => {
  status.value = STATUS.LOADING
  issue.value = null

  learningApiIssue.getCertificateIssue({
    custom: {
      params: { token: props.token },
      isPublic: true
    }
  }).then((res) => {
    issue.value = res.data
    status.value = STATUS.VALID
  }).catch((err) => {
    console.error('getCertificateIssue err', err)
    const httpStatus = err.response?.status
    if (httpStatus === 404 || httpStatus === 400 || httpStatus === 410) {
      status.value = STATUS.INVALID
    } else {
      status.value = STATUS.ERROR
    }
  })
}

onMounted(() => {
  if (!props.token) {
    status.value = STATUS.INVALID
    return
  }
  fetchIssue()
})
</script>

<template>
  <div class="container">
    <div
      v-if="status === STATUS.LOADING"
      class="panel"
    >
      <p class="panel__message">確認中です。しばらくお待ちください。</p>
    </div>

    <div
      v-else-if="status === STATUS.VALID"
      class="certificate"
    >
      <div class="certificate__header">

        <dl class="certificate__info">
          <div class="certificate__row">
            <dt>修了証</dt>
            <dd>{{ certificateNo }}</dd>
          </div>
          <div class="certificate__row">
            <dt>氏名</dt>
            <dd>{{ traineeName }}</dd>
          </div>
          <div class="certificate__row">
            <dt>住所</dt>
            <dd>{{ traineePref }}</dd>
          </div>
        </dl>
      </div>

      <div class="certificate__body">
        <p>
          本 PDF は、一般社団法人日本コンタクトレンズ協会が付与した固有のデジタル認証情報に基づき、当該修了証が正式に発行された真正な原本であることを証明するものです。
        </p>
        <p>
          QR コードの読み取りにより照合された認証データは、発行時点の情報と一致しており、内容に改ざん等がないことを確認しております。
        </p>
        <p>
          受講者各位におかれましては、本認証結果を必要に応じてご提示いただくことで、修了証の正当性を第三者に証明することが可能です。
        </p>
      </div>

      <div class="certificate__logo-wrap">
        <img
          class="certificate__logo"
          src="@assets/images/header_rogo.png"
          alt="一般社団法人日本コンタクトレンズ協会"
        >
      </div>
    </div>

    <div
      v-else-if="status === STATUS.INVALID"
      class="panel panel--invalid"
    >
      <p class="panel__message">
        修了証を確認できませんでした。<br>
        QRコードが無効、または有効期限が切れている可能性があります。
      </p>
    </div>

    <div
      v-else
      class="panel panel--invalid"
    >
      <p class="panel__message">
        通信エラーが発生しました。時間をおいて再度お試しください。
      </p>
      <div class="btnContainer">
        <button
          type="button"
          class="c-button c-button--medium c-button--border"
          @click="fetchIssue"
        >
          再読み込み
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 16px 60px;
}

.certificate {
  color: #000;
  font-size: 16px;
  line-height: 1.8;
  border-radius: 4px;
  padding: 40px 10px;

  &__header {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
  }

  &__info {
    width: auto;
  }

  &__row {
    display: flex;
    margin-bottom: 8px;

    dt {
      flex-shrink: 0;
      width: 4.5em;
      margin: 0;
      font-weight: normal;
      font-size: 18px;
    }

    dd {
      margin: 0;
    }
  }

  &__body {
    margin-bottom: 80px;

    p {
      margin: 0 0 1.8em;
      text-align: left;
      font-size: 18px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__logo-wrap {
    display: flex;
    justify-content: flex-end;
  }

  &__logo {
    display: block;
    width: 100%;
    height: 50px;
    border-style: none;
    object-fit: contain;
    object-position: right center;
  }
}

.panel {
  padding: 24px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;

  &__message {
    font-size: 16px;
    line-height: 1.7;
    text-align: center;
  }

  &--invalid {
    border-color: #c62828;
    background: #fff8f8;
  }
}

.btnContainer {
  display: flex;
  justify-content: center;
  margin-top: 20px;

  button {
    width: 200px;
  }
}

@media screen and (max-width: 768px) {
  .certificate__logo {
    width: 165px;
    height: 37.5px;
  }
}
</style>
